import Client from '../../../cms/client'
import { BulkFile } from '../../../cms/models/BulkFile'
import { CMSFile } from '../../../cms/models/File'
import { Strings } from '../../../utils/strings'
import { setRecoil } from '../../../state/recoilNexus'
import { cacheBuster } from '../../../state/state'
import { INTERNATIONAL_STREAM } from '../../../utils/constants'
import { parseFileName, parseTemplate } from './bulkFileParser'
import { BulkFileStore } from './bulkFileStore'

/**
 * Orchestrates bulk upload workflows: file uploads and template parsing.
 * Keeps BulkComponent thin by encapsulating side-effect-heavy logic here.
 */
export const BulkUploadService = {
	/**
	 * Valid file extensions for bulk upload.
	 */
	validExtensions: [
		...CMSFile.Extensions.PDF,
		...CMSFile.Extensions.CSV,
		...CMSFile.Extensions.Excel,
		...CMSFile.Extensions.MathExcel,
		...CMSFile.Extensions.Doc,
		...CMSFile.Extensions.PPT,
		...CMSFile.Extensions.ZIP,
		...CMSFile.Extensions.Audio
	],

	/**
	 * Process a FileList from drag-drop or file picker.
	 * Separates the template file from regular files and handles each path.
	 */
	processFiles(list: FileList, businessStream: string): void {
		const files: File[] = []

		for (let i = 0; i < list.length; i++) {
			const file = list[i]
			const ext = Strings.default(file.name.split('.').pop()).toLowerCase()
			if (this.validExtensions.includes(ext)) {
				files.push(file)
			}
		}

		if (files.length === 0) {
			return
		}

		// Separate template from regular files
		const templateName = `BulkImport-${businessStream}.xlsx`
		const templateFile = files.find((f) => f.name === templateName)
		const regularFiles = files.filter((f) => f.name !== templateName)

		// Process template first (if present) so that when regular file uploads
		// complete and call setData, the BulkFile entry already has template data.
		if (templateFile) {
			this.processTemplate(templateFile, businessStream)
		}

		regularFiles.forEach((file) => {
			this.uploadRegularFile(file, businessStream)
		})
	},

	/**
	 * Parse and apply an Excel template file to the bulk store.
	 */
	processTemplate(file: File, businessStream: string): void {
		const [parseResult] = Client.parseTemplateFile(file)
		parseResult.then((excelRows) => {
			parseTemplate(excelRows, businessStream).then((templateFiles) => {
				templateFiles.forEach((templateFile) => {
					BulkFileStore.addOrUpdate(templateFile, (existing) => {
						existing.filename = templateFile.filename
						if (existing.data.file) {
							templateFile.data.file = existing.data.file
						}
						existing.data = templateFile.data
						existing.setData({}, true)
						setRecoil(
							cacheBuster(`bulk-file-${templateFile.filename}`),
							(val) => val + 1
						)
					})
				})
			})
		})
	},

	/**
	 * Upload a regular (non-template) file to the bulk store.
	 */
	uploadRegularFile(file: File, businessStream: string): void {
		let f: BulkFile
		if (businessStream === INTERNATIONAL_STREAM) {
			f = parseFileName(file.name)
		} else {
			f = new BulkFile({ filename: file.name })
		}

		const newFile = BulkFileStore.addOrUpdate(f, (existing) => {
			existing.uploadFile(file)
		})
		if (newFile.bulkid < 1) {
			newFile.uploadFile(file)
		}
	}
}
